import { createApi } from '@reduxjs/toolkit/query/react';

import { accountHeaderWithRetryBQ } from './base-queries/account-header-with-retry';

const { app } = PULSE;

// RTK Query update: Define an api service with a base URL and retry attempts
// RTK Query update: Endpoints can be added here or injected in components
export const aflApi = createApi({
    reducerPath: 'aflAPI',
    baseQuery: accountHeaderWithRetryBQ(app.environment.api),
    endpoints: (build) => ({
        getCompetitions: build.query({
            query: () => 'competitions?pageSize=50',
            transformResponse: (response) => response?.competitions
        }),
        getCompSeasons: build.query({
            query: ({ competitionId, params = { pageSize: 20 } }) =>
                `competitions/${competitionId}/compseasons${app.common.prepareParams(
                    params
                )}`,
            transformResponse: (response, meta, arg) =>
                response?.compSeasons
                    ? response.compSeasons.map((item) => ({
                          ...item,
                          competitionId: arg.competitionId
                      }))
                    : []
        }),
        getCompSeason: build.query({
            query: ({ id }) => `compseasons/${id}`,
            transformResponse: (response) => response?.compSeasons?.[0]
        }),
        getTeams: build.query({
            query: (compSeasonId) =>
                `teams?compSeasonId=${compSeasonId}&pageSize=100`,
            transformResponse: (response) =>
                response?.teams?.filter(
                    (team) =>
                        !PULSE.app.common.team.isPlaceholderTeam(
                            team.providerId
                        )
                )
        }),
        getMatches: build.query({
            query: (params) => `matches${app.common.prepareParams(params)}`,
            transformResponse: (response, params, arg) => {
                arg.matchStatus &&
                    response?.matches.filter(
                        (match) => match.status === arg.matchStatus
                    );
                return response?.matches;
            }
        }),
        getMatch: build.query({
            query: ({ id }) => `matches/${id}`,
            transformResponse: (response) => response?.matches
        }),
        getLadder: build.query({
            query: ({ compSeasonId, params }) =>
                `compseasons/${compSeasonId}/ladders${app.common.prepareParams(
                    params
                )}`
        }),
        getRounds: build.query({
            query: ({ compseasonId, params }) =>
                `compseasons/${compseasonId}/rounds${app.common.prepareParams(
                    params
                )}`,
            transformResponse: (response, params, arg) =>
                response?.rounds
                    ? response.rounds.map((item) => ({
                          ...item,
                          seasonId: arg.compseasonId
                      }))
                    : []
        }),
        getVenues: build.query({
            query: (compSeasonId) =>
                `venues?compSeasonId=${compSeasonId}&pageSize=100`,
            transformResponse: (response) => response?.venues
        }),
        getBrownlowPredictor: build.query({
            query: ({ compSeasonId, page = 0, team = '', players = '' }) =>
                `/compseasons/${compSeasonId}/award/brownlow?page=${page}&pageSize=20${
                    team.length ? `&team=${team}` : ''
                }${players.length ? `&players=${players}` : ''}`,
            transformResponse: (response) => {
                return {
                    results: response?.players.slice(0, 20) || [],
                    newItems: response?.players || [],
                    pageInfo: response?.pageInfo,
                    compSeason: response?.compSeason
                };
            },
            serializeQueryArgs: ({ queryArgs }) => {
                return `compSeasonId-${queryArgs.compSeasonId}-page-${
                    queryArgs.page
                }${queryArgs.team?.length ? '-team-' + queryArgs.team : ''}${
                    queryArgs.players?.length
                        ? '-players-' + queryArgs.players
                        : ''
                }`;
            }
        }),
        getBrownlowPredictorFavourites: build.query({
            query: ({ compSeasonId, players = '' }) =>
                `/compseasons/${compSeasonId}/award/brownlow?${
                    players.length ? `&players=${players}` : ''
                }`,
            transformResponse: (response) => {
                return response?.players || [];
            }
        }),
        getBrownlowPredictorSearch: build.query({
            query: ({ compSeasonId, term = '' }) =>
                `/compseasons/${compSeasonId}/award/brownlow/player-search?page=0&pageSize=50${
                    term !== '' ? `&term=${encodeURIComponent(term)}` : ''
                }`,
            transformResponse: (response) => {
                return {
                    results: response?.players || [],
                    newItems: response?.players || []
                };
            }
        }),
        getMappedMatches: build.query({
            query: () => `matches/idmap`,
            transformResponse: (response) => response?.idMapResponse?.ids
        }),
        getMappedTeams: build.query({
            query: () => `teams/idmap`,
            transformResponse: (response) => response?.idMapResponse?.ids
        })
    })
});

export const {
    useGetCompetitionsQuery,
    useGetCompSeasonsQuery,
    useGetCompSeasonQuery,
    useGetTeamsQuery,
    useGetMatchesQuery,
    useGetMatchQuery,
    useGetLadderQuery,
    useGetRoundsQuery,
    useGetVenuesQuery,
    useGetBrownlowPredictorQuery,
    useGetBrownlowPredictorSearchQuery,
    useGetBrownlowPredictorFavouritesQuery,
    useGetMappedMatchesQuery,
    useGetMappedTeamsQuery
} = aflApi;
